import type {
  IMatchScheduleItem,
  IScheduleConstraints,
  IScheduleDayConstraints,
} from '../../../../../types/schedule';
import { getDateString } from '../schedule.helpers';
import { getScheduleLaidOutRowHeightPx } from '../../../../../features/schedule/scheduleZoomBounds';
import {
  getScheduleBodyYFromDayStartMs,
  GRID_SLOT_MINUTES,
} from './scheduleMatchLayout';

export type GroupDragOverlayItem = {
  match: IMatchScheduleItem;
  left: number;
  top: number;
  width: number;
  height: number;
};

export type GroupDragOverlayLayout = {
  items: GroupDragOverlayItem[];
  width: number;
  height: number;
};

function findDayForScheduledMatch(
  match: IMatchScheduleItem,
  constraints: IScheduleConstraints,
  timeZone: string
): IScheduleDayConstraints | null {
  if (!match.scheduleDate) return null;
  const matchDayStr = getDateString(match.scheduleDate, timeZone);
  return (
    constraints.days.find((d) => getDateString(d.startTime, timeZone) === matchDayStr) ?? null
  );
}

export function buildGroupDragOverlayLayout(params: {
  anchor: IMatchScheduleItem;
  peerMatches: IMatchScheduleItem[];
  constraints: IScheduleConstraints;
  timeZone: string;
  scale: number;
  courtWidth: number;
}): GroupDragOverlayLayout | null {
  const { anchor, peerMatches, constraints, timeZone, scale, courtWidth } = params;
  if (!anchor.scheduleDate || !anchor.court) return null;

  const day = findDayForScheduledMatch(anchor, constraints, timeZone);
  if (!day) return null;

  const anchorDayStr = getDateString(anchor.scheduleDate, timeZone);
  const sameDayPeers = peerMatches.filter(
    (m) =>
      m.scheduleDate &&
      m.court &&
      getDateString(m.scheduleDate, timeZone) === anchorDayStr
  );
  const courtIds = new Set(day.courts.map((c) => c.id));
  const allMatches = [anchor, ...sameDayPeers].filter(
    (m) => m.court?.id != null && courtIds.has(m.court.id)
  );

  const dayStartMs = new Date(day.startTime).getTime();
  const anchorStartMs = new Date(anchor.scheduleDate).getTime();
  const anchorCourtIdx = day.courts.findIndex((c) => c.id === anchor.court!.id);
  if (anchorCourtIdx < 0) return null;

  const slotHeight = getScheduleLaidOutRowHeightPx(scale);
  const anchorTopY = getScheduleBodyYFromDayStartMs(anchorStartMs, dayStartMs, scale);

  const rawItems: GroupDragOverlayItem[] = allMatches.map((match) => {
    const courtIdx = day.courts.findIndex((c) => c.id === match.court!.id);
    const startMs = new Date(match.scheduleDate!).getTime();
    const duration = match.duration || 20;
    const left = (courtIdx - anchorCourtIdx) * courtWidth;
    const top = getScheduleBodyYFromDayStartMs(startMs, dayStartMs, scale) - anchorTopY;
    const height = (duration / GRID_SLOT_MINUTES) * slotHeight;
    return { match, left, top, width: courtWidth, height };
  });

  const minLeft = Math.min(0, ...rawItems.map((i) => i.left));
  const minTop = Math.min(0, ...rawItems.map((i) => i.top));
  const maxRight = Math.max(...rawItems.map((i) => i.left + i.width));
  const maxBottom = Math.max(...rawItems.map((i) => i.top + i.height));

  const items = rawItems.map((i) => ({
    ...i,
    left: i.left - minLeft,
    top: i.top - minTop,
  }));

  return {
    items,
    width: maxRight - minLeft,
    height: maxBottom - minTop,
  };
}
